import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useTeamSlug } from '@/context/TeamSlugContext'
import { useToast } from '@/components/ui/use-toast'
import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { useTheme } from 'next-themes'

import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import {
	LogOut,
	User,
	Sun,
	Menu,
	X,
	Moon,
	Map,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'

interface TeamForHeader {
	name: string
	logo_url?: string | null
	slug: string
}

interface RoadmapForHeader {
	name: string
	slug: string
	logo_url?: string | null
}

interface PublicHeaderProps {
	team?: TeamForHeader | null
	roadmap?: RoadmapForHeader | null
	onSignInClick: () => void
}

const getInitials = (name: string | undefined | null): string => {
	if (!name) return 'A'
	return name
		.split(' ')
		.map((part) => part[0])
		.join('')
		.toUpperCase()
}

export function PublicHeader({
	team,
	roadmap,
	onSignInClick,
}: PublicHeaderProps) {
	const { t } = useTranslation()
	const { session, user, signOut, loading: authLoading } = useAuth()
	const { isPortal } = useTeamSlug()
	const navigate = useNavigate()
	const location = useLocation()
	const { toast } = useToast()
	const [isOpen, setIsOpen] = useState(false)
	const { theme, setTheme } = useTheme()

	const toggleMenu = () => setIsOpen(!isOpen)

	const toggleTheme = () => {
		setTheme(theme === 'dark' ? 'light' : 'dark')
	}

	const handleSignOut = async () => {
		try {
			await signOut()
			// Navigate to home page - either subdomain home or path-based home
			navigate(isPortal ? '/' : `/c/${team?.slug || ''}`, { replace: true })
			toast({
				title: t('publicHeader.signedOut'),
				description: t('publicHeader.successfullySignedOut'),
			})
		} catch (error: any) {
			toast({
				variant: 'destructive',
				title: t('publicHeader.error'),
				description: error.message || t('publicHeader.failedToSignOut'),
			})
		}
	}

	const navItems = [
		{
			label: t('publicHeader.roadmap'),
			href: isPortal ? '/roadmap' : `/c/${team?.slug}/roadmap`,
			icon: Map,
		},
	]

	const renderAuthControls = () => {
		if (authLoading) {
			return (
				<motion.div
					initial={{ opacity: 0, scale: 0.8 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.3 }}>
					<Skeleton className="h-10 w-10 rounded-full" />
				</motion.div>
			)
		}

		if (!session || !user) {
			return (
				<motion.div
					initial={{ opacity: 0, x: 20 }}
					animate={{ opacity: 1, x: 0 }}
					transition={{ duration: 0.4, delay: 0.2 }}
					whileHover={{ scale: 1.02 }}
					whileTap={{ scale: 0.98 }}>
					<Button
						onClick={onSignInClick}
						size="sm"
						className="relative inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-gray-900 to-gray-700 rounded-full hover:from-gray-800 hover:to-gray-600 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 active:translate-y-0">
						<span className="relative z-10">{t('publicHeader.signIn')}</span>
						<div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
					</Button>
				</motion.div>
			)
		}

		const userName = user.user_metadata?.full_name || user.email

		return (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<motion.div
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
						transition={{ type: 'spring', stiffness: 400, damping: 25 }}>
						<Button
							variant="ghost"
							className="relative h-9 w-9 rounded-full hover:bg-gray-100/80 dark:hover:bg-slate-800/80 transition-all duration-300 ring-2 ring-transparent hover:ring-gray-200 dark:hover:ring-slate-700 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-slate-600">
							<Avatar className="h-8 w-8 shadow-sm">
								{user.user_metadata?.avatar_url && (
									<AvatarImage
										src={user.user_metadata.avatar_url}
											alt={userName || t('publicHeader.user')}
										className="object-cover"
									/>
								)}
								<AvatarFallback className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-slate-700 dark:to-slate-800 text-gray-700 dark:text-gray-300 text-xs font-semibold">
									{getInitials(userName)}
								</AvatarFallback>
							</Avatar>
						</Button>
					</motion.div>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					className="w-64 p-2 shadow-xl border-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg rounded-xl"
					align="end"
					forceMount>
					<DropdownMenuLabel className="font-normal p-3">
						<div className="flex flex-col space-y-2">
							<p className="text-sm font-semibold leading-none text-gray-900 dark:text-white">
								{userName}
							</p>
							<p className="text-xs leading-none text-gray-500 dark:text-gray-400 truncate">
								{user.email}
							</p>
						</div>
					</DropdownMenuLabel>
					<DropdownMenuSeparator className="my-2 bg-gray-200 dark:bg-slate-700" />
					<DropdownMenuItem asChild>
						<Link
							to={isPortal ? '/profile' : `/c/${team?.slug}/profile`}
							className="cursor-pointer rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors duration-200">
							<User className="mr-3 h-4 w-4" />
							<span>{t('publicHeader.myProfile')}</span>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuSeparator className="my-2 bg-gray-200 dark:bg-slate-700" />
					<DropdownMenuItem
						onSelect={handleSignOut}
						className="cursor-pointer text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-lg p-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200">
						<LogOut className="mr-3 h-4 w-4" />
						<span>{t('publicHeader.signOut')}</span>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		)
	}

	return (
		<>
			{/* Compact Mobile Header - Fixed Position */}
			<motion.header
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6, ease: 'easeOut' }}
				className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-b border-gray-200/50 dark:border-slate-700/50 md:relative md:top-auto md:bg-transparent md:border-0">
				{/* Desktop Header - Original Design */}
				<div className="hidden md:flex justify-center w-full py-8 px-4">
					<div className="absolute inset-0 bg-gradient-to-b from-background/50 to-transparent pointer-events-none" />

					<div className="flex items-center justify-between px-8 py-4 bg-background/80 backdrop-blur-xl rounded-2xl w-full max-w-6xl relative z-10 border border-gray-200/50 dark:border-slate-700/50">
						{/* Logo and Roadmap Name */}
						<div className="flex items-center">
							<Link
								to={isPortal ? '/' : `/c/${team?.slug || ''}`}
								className="flex items-center gap-4 hover:opacity-90 transition-all duration-300 group">
								<motion.div
									className="relative"
									initial={{ scale: 0.8, rotate: -10 }}
									animate={{ scale: 1, rotate: 0 }}
									whileHover={{ rotate: 5, scale: 1.05 }}
									transition={{
										duration: 0.4,
										type: 'spring',
										stiffness: 300,
									}}>
									{roadmap?.logo_url || team?.logo_url ? (
										<div className="relative">
											<img
												src={roadmap?.logo_url || team?.logo_url || ''}
												alt={`${roadmap?.name || team?.name} Logo`}
												className="h-10 w-10 rounded-xl object-contain shadow-lg ring-2 ring-white/20 dark:ring-slate-600/20"
												onError={(e) => {
													// Fallback to team logo if roadmap logo fails
													const target = e.target as HTMLImageElement
													if (
														roadmap?.logo_url &&
														team?.logo_url &&
														target.src !== team.logo_url
													) {
														target.src = team.logo_url
													}
												}}
											/>
											<div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/20 to-transparent" />
										</div>
									) : (
										<div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-400 via-red-400 to-pink-400 flex items-center justify-center font-bold text-white text-sm shadow-lg ring-2 ring-white/20 dark:ring-slate-600/20 relative overflow-hidden">
											<span className="relative z-10">
												{getInitials(roadmap?.name || team?.name)}
											</span>
											<div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent" />
										</div>
									)}
								</motion.div>
								<motion.span
									className="text-gray-900 dark:text-white font-bold text-xl tracking-tight group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors duration-300"
									initial={{ opacity: 0, x: -10 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.5, delay: 0.2 }}>
									{roadmap?.name || team?.name || t('publicHeader.portal')}
								</motion.span>
							</Link>
						</div>

						{/* Desktop Navigation */}
						<nav className="flex items-center space-x-2">
							{navItems.map((item, index) => {
								const isActive = location.pathname === item.href
								return (
									<motion.div
										key={item.href}
										initial={{ opacity: 0, y: -10 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.4, delay: index * 0.1 + 0.3 }}>
										<Link
											to={item.href}
											className={cn(
												'relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 group overflow-hidden',
												isActive
													? 'bg-gradient-to-r from-gray-100 to-gray-50 dark:from-slate-800 dark:to-slate-700 text-gray-900 dark:text-white '
													: 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-800/50'
											)}>
											<motion.div
												whileHover={{ scale: 1.1, rotate: 5 }}
												transition={{
													type: 'spring',
													stiffness: 400,
													damping: 25,
												}}>
												<item.icon className="h-4 w-4" />
											</motion.div>
											<span className="relative z-10">{item.label}</span>
											{isActive && (
												<motion.div
													layoutId="activeTab"
													className="absolute inset-0 bg-gradient-to-r from-gray-200/50 to-gray-100/50 dark:from-slate-700/50 dark:to-slate-600/50 rounded-xl"
													transition={{
														type: 'spring',
														bounce: 0.2,
														duration: 0.6,
													}}
												/>
											)}
											<div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
										</Link>
									</motion.div>
								)
							})}
						</nav>

						{/* Desktop Auth Controls & Theme Toggle */}
						<div className="flex items-center gap-3">
							<LanguageSwitcher />
							{renderAuthControls()}
							<motion.div
								whileHover={{ scale: 1.05, rotate: 15 }}
								whileTap={{ scale: 0.95 }}
								transition={{ type: 'spring', stiffness: 400, damping: 25 }}>
								<Button
									variant="ghost"
									size="icon"
									onClick={toggleTheme}
									className="h-10 w-10 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition-all duration-300 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-slate-600">
									{theme === 'dark' ? (
										<Sun className="h-4 w-4" />
									) : (
										<Moon className="h-4 w-4" />
									)}
								</Button>
							</motion.div>
						</div>
					</div>
				</div>

				{/* Mobile Header - New Compact Design */}
				<div className="md:hidden px-4 py-3">
					<div className="flex items-center justify-between">
						{/* Logo and Roadmap Name - Compact */}
						<Link
							to={isPortal ? '/' : `/c/${team?.slug || ''}`}
							className="flex items-center gap-3 hover:opacity-90 transition-all duration-300 group min-w-0 flex-1">
							<motion.div
								className="relative flex-shrink-0"
								whileHover={{ rotate: 5, scale: 1.05 }}
								transition={{ duration: 0.4, type: 'spring', stiffness: 300 }}>
								{roadmap?.logo_url || team?.logo_url ? (
									<div className="relative">
										<img
											src={roadmap?.logo_url || team?.logo_url || ''}
											alt={`${roadmap?.name || team?.name} Logo`}
											className="h-8 w-8 rounded-lg object-contain shadow-sm ring-1 ring-white/20 dark:ring-slate-600/20"
											onError={(e) => {
												const target = e.target as HTMLImageElement
												if (
													roadmap?.logo_url &&
													team?.logo_url &&
													target.src !== team.logo_url
												) {
													target.src = team.logo_url
												}
											}}
										/>
									</div>
								) : (
									<div className="h-8 w-8 rounded-lg bg-gradient-to-br from-orange-400 via-red-400 to-pink-400 flex items-center justify-center font-bold text-white text-xs shadow-sm ring-1 ring-white/20 dark:ring-slate-600/20">
										<span className="relative z-10">
											{getInitials(roadmap?.name || team?.name)}
										</span>
									</div>
								)}
							</motion.div>

							<div className="min-w-0 flex-1">
								<motion.h1
									className="text-gray-900 dark:text-white font-semibold text-lg tracking-tight group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors duration-300 truncate"
									initial={{ opacity: 0, x: -10 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.5, delay: 0.1 }}>
									{roadmap?.name || team?.name || t('publicHeader.portal')}
								</motion.h1>
								<motion.p
									className="text-xs text-gray-500 dark:text-gray-400 truncate"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ duration: 0.5, delay: 0.2 }}>
									{t('publicHeader.roadmap')}
								</motion.p>
							</div>
						</Link>

						{/* Mobile Actions */}
						<div className="flex items-center gap-2 flex-shrink-0">
							{/* Theme Toggle */}
							<motion.div
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}>
								<Button
									variant="ghost"
									size="icon"
									onClick={toggleTheme}
									className="h-9 w-9 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-all duration-300">
									{theme === 'dark' ? (
										<Sun className="h-4 w-4" />
									) : (
										<Moon className="h-4 w-4" />
									)}
								</Button>
							</motion.div>

							{/* Auth Controls - Mobile */}
							<div className="hidden sm:block">{renderAuthControls()}</div>

							{/* Mobile Menu Button */}
							<motion.button
								className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100/80 dark:hover:bg-slate-800/80 transition-all duration-300 ml-1"
								onClick={toggleMenu}
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
								aria-label="Toggle menu">
								<AnimatePresence mode="wait">
									{isOpen ? (
										<motion.div
											key="close"
											initial={{ opacity: 0, rotate: -90 }}
											animate={{ opacity: 1, rotate: 0 }}
											exit={{ opacity: 0, rotate: 90 }}
											transition={{ duration: 0.2 }}>
											<X className="h-5 w-5 text-gray-900 dark:text-white" />
										</motion.div>
									) : (
										<motion.div
											key="menu"
											initial={{ opacity: 0, rotate: 90 }}
											animate={{ opacity: 1, rotate: 0 }}
											exit={{ opacity: 0, rotate: -90 }}
											transition={{ duration: 0.2 }}>
											<Menu className="h-5 w-5 text-gray-900 dark:text-white" />
										</motion.div>
									)}
								</AnimatePresence>
							</motion.button>
						</div>
					</div>
				</div>
			</motion.header>

			{/* Mobile Menu Slide-out Drawer */}
			<AnimatePresence>
				{isOpen && (
					<>
						{/* Backdrop */}
						<motion.div
							className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-40 md:hidden"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.3 }}
							onClick={toggleMenu}
						/>

						{/* Slide-out Drawer */}
						<motion.div
							className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-background/98 backdrop-blur-xl z-50 border-l border-gray-200/50 dark:border-slate-700/50 md:hidden overflow-y-auto"
							initial={{ opacity: 0, x: '100%' }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: '100%' }}
							transition={{ type: 'spring', damping: 25, stiffness: 300 }}>
							{/* Drawer Header */}
							<div className="flex items-center justify-between p-4 border-b border-gray-200/50 dark:border-slate-700/50">
								<div className="flex items-center gap-3">
									<div className="h-8 w-8 rounded-lg bg-gradient-to-br from-orange-400 via-red-400 to-pink-400 flex items-center justify-center font-bold text-white text-xs">
										<span>{getInitials(roadmap?.name || team?.name)}</span>
									</div>
									<div>
										<h2 className="font-semibold text-gray-900 dark:text-white">
											{roadmap?.name || team?.name || t('publicHeader.portal')}
										</h2>
										<p className="text-xs text-gray-500 dark:text-gray-400">
											{t('publicHeader.navigation')}
										</p>
									</div>
								</div>
								<Button
									variant="ghost"
									size="icon"
									onClick={toggleMenu}
									className="h-8 w-8 rounded-lg">
									<X className="h-4 w-4" />
								</Button>
							</div>

							{/* Navigation Items */}
							<div className="p-4 space-y-2">
								{navItems.map((item, i) => {
									const isActive = location.pathname === item.href
									return (
										<motion.div
											key={item.href}
											initial={{ opacity: 0, x: 20 }}
											animate={{ opacity: 1, x: 0 }}
											transition={{ delay: i * 0.1 + 0.1, duration: 0.4 }}
											exit={{ opacity: 0, x: 20 }}>
											<Link
												to={item.href}
												className={cn(
													'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 relative overflow-hidden group',
													isActive
														? 'bg-gradient-to-r from-gray-100 to-gray-50 dark:from-slate-800 dark:to-slate-700 text-gray-900 dark:text-white shadow-sm'
														: 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800/50 hover:text-gray-900 dark:hover:text-white'
												)}
												onClick={toggleMenu}>
												<motion.div
													whileHover={{ scale: 1.1, rotate: 5 }}
													transition={{
														type: 'spring',
														stiffness: 400,
														damping: 25,
													}}>
													<item.icon className="h-5 w-5" />
												</motion.div>
												<span className="relative z-10">{item.label}</span>
												<div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
											</Link>
										</motion.div>
									)
								})}
							</div>

							{/* Settings Section */}
							<div className="p-4 border-t border-gray-200/50 dark:border-slate-700/50">
								<motion.div
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.3, duration: 0.4 }}
									exit={{ opacity: 0, y: 20 }}
									className="space-y-3">
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
											{t('publicHeader.language') || 'Language'}
										</span>
										<LanguageSwitcher />
									</div>
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
											{t('publicHeader.theme') || 'Theme'}
										</span>
										<Button
											variant="ghost"
											size="icon"
											onClick={toggleTheme}
											className="h-9 w-9 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg">
											{theme === 'dark' ? (
												<Sun className="h-4 w-4" />
											) : (
												<Moon className="h-4 w-4" />
											)}
										</Button>
									</div>
								</motion.div>
							</div>

							{/* User Section */}
							<div className="p-4 border-t border-gray-200/50 dark:border-slate-700/50 mt-auto">
								<motion.div
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.4, duration: 0.4 }}
									exit={{ opacity: 0, y: 20 }}>
									{!session || !user ? (
										<Button
											onClick={() => {
												onSignInClick()
												toggleMenu()
											}}
											className="w-full py-3 text-sm font-medium text-white bg-gradient-to-r from-gray-900 to-gray-700 rounded-xl hover:from-gray-800 hover:to-gray-600 transition-all duration-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 active:translate-y-0">
											{t('publicHeader.signIn')}
										</Button>
									) : (
										<div className="space-y-3">
											<div className="flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100/50 dark:from-slate-800 dark:to-slate-700/50 rounded-xl">
												<Avatar className="h-10 w-10 shadow-sm">
													{user.user_metadata?.avatar_url && (
														<AvatarImage
															src={user.user_metadata.avatar_url}
															alt={
																user.user_metadata?.full_name ||
																user.email ||
																t('publicHeader.user')
															}
															className="object-cover"
														/>
													)}
													<AvatarFallback className="bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-800 text-gray-700 dark:text-gray-300 text-sm font-semibold">
														{getInitials(
															user.user_metadata?.full_name || user.email
														)}
													</AvatarFallback>
												</Avatar>
												<div className="flex-1 min-w-0">
													<p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
														{user.user_metadata?.full_name || user.email}
													</p>
													<p className="text-xs text-gray-500 dark:text-gray-400 truncate">
														{user.email}
													</p>
												</div>
											</div>
											<Link
												to={isPortal ? '/profile' : `/c/${team?.slug}/profile`}
												className="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-800/50 rounded-lg transition-all duration-300 text-sm"
												onClick={toggleMenu}>
												<User className="h-4 w-4" />
												<span>{t('publicHeader.myProfile')}</span>
											</Link>
											<button
												onClick={() => {
													handleSignOut()
													toggleMenu()
												}}
												className="flex items-center gap-3 px-3 py-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-300 w-full text-sm">
												<LogOut className="h-4 w-4" />
												<span>{t('publicHeader.signOut')}</span>
											</button>
										</div>
									)}
								</motion.div>
							</div>
						</motion.div>
					</>
				)}
			</AnimatePresence>

			{/* Mobile spacer to prevent content from hiding under fixed header */}
			<div className="h-16 md:hidden" />
		</>
	)
}
